import {
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  AlertService,
  AlertType,
  ButtonConfig,
  ButtonType,
  DynamicFieldDirective,
  equalToValidator,
  FieldType,
  GenericButtonComponent,
  HeaderService,
  ModalFieldConfig,
  ModalMode,
} from '@solcre-org/core-ui';
import { ChangeMyPasswordForm } from './change-my-password-form.interface';
import { BuildFormFromFieldsDirective } from '../../../shared/directives/build-form-from-fields.directive';
import { JsonPipe } from '@angular/common';
import { ApiService, ArrayUtility, AuthService } from '@solcre-org/core';
import { environment } from '../../../../environments/environment.local';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-change-my-password',
  imports: [
    GenericButtonComponent,
    DynamicFieldDirective,
    BuildFormFromFieldsDirective,
    ReactiveFormsModule,
    TranslateModule,
  ],
  templateUrl: './change-my-password.html',
})
export class ChangeMyPassword implements OnInit, OnDestroy {
  //Inject services
  apiService: ApiService = inject(ApiService);
  authService: AuthService = inject(AuthService);
  headerService: HeaderService = inject(HeaderService);
  alertService: AlertService = inject(AlertService);
  translateService: TranslateService = inject(TranslateService);

  //Models
  ModalMode = ModalMode;
  saveButtonConfig = computed<ButtonConfig>(() => ({
    type: ButtonType.PRIMARY,
    text: 'changeMyPassword.save',
    icon: 'icon-save',
    iconPosition: 'left',
    disabled: this.isSaving(),
    loading: this.isSaving(),
  }));
  isSaving = signal<boolean>(false);
  form: FormGroup = new FormGroup({});

  get passwordFields(): ModalFieldConfig<ChangeMyPasswordForm>[] {
    return [
      {
        key: 'currentPassword',
        label: 'changeMyPassword.currentPassword',
        placeholder: 'changeMyPassword.placeholder',
        type: FieldType.PASSWORD,
        readonly: false,
        validators: [Validators.required],
        errorMessages: {
          required: 'validatorss.required',
        },
      },
      {
        key: 'newPassword',
        label: 'changeMyPassword.newPassword',
        placeholder: 'changeMyPassword.placeholder',
        type: FieldType.PASSWORD,
        readonly: false,
        validators: [Validators.required, Validators.minLength(12)],
        errorMessages: {
          required: 'validatorss.required',
          minlength: 'validators.minLength',
        },
      },
      {
        key: 'newPasswordConfirmation',
        label: 'changeMyPassword.confirmPassword',
        placeholder: 'changeMyPassword.placeholder',
        type: FieldType.PASSWORD,
        readonly: false,
        validators: [Validators.required, equalToValidator('newPassword')],
        errorMessages: {
          required: 'validators.required',
          equalTo: 'validators.passwordsDontMatch',
        },
      },
    ];
  }

  ngOnInit(): void {
    this.setHeaderTitle();
  }

  ngOnDestroy(): void {
    this.headerService.clearCustomActions();
  }

  onSave(): void {
    if (this.form.valid && !this.isSaving()) {
      this.isSaving.set(true);
      this.apiService
        .createObj(environment.API.CHANGE_PASSWORD, this.form.getRawValue())
        .subscribe({
          next: () => {
            this.isSaving.set(false);
            this.alertService.show({
              type: AlertType.SUCCESS,
              message: 'changeMyPassword.success',
              duration: 2000,
              inBottom: true,
            });

            //Logout
            setTimeout(() => this.authService.logout(), 1000);
          },
          error: (error: HttpErrorResponse) => {
            this.isSaving.set(false);
            this.alertService.show({
              type: AlertType.ERROR,
              message: error.error?.detail ?? `errors.generic`,
              duration: 2000,
              inBottom: true,
            });
          },
        });
    }
  }

  getFieldErrors(key: string): string[] {
    const errors: string[] = [];
    const control: FormControl | null = <FormControl>this.form.get(key);
    if (control && !control.valid && control.touched) {
      ArrayUtility.each(Object.keys(control.errors ?? {}), (error: string) => {
        let message = 'validators.required';
        let params = {};

        if (error === 'minlength') {
          message = 'validators.minLength';
          params = { minLength: 12 };
        } else if (error === 'equalTo') {
          message = 'validators.passwordsDontMatch';
        }

        errors.push(this.translateService.instant(message, params));
      });
    }
    return errors;
  }

  onValueChange(key: string, value: string): void {
    this.form.patchValue({ [key]: value });
  }

  onBlur(key: string): void {
    this.form.get(key)?.markAsTouched();
  }

  private setHeaderTitle(): void {
    this.translateService.get('changeMyPassword.title').subscribe((title) => {
      this.headerService.setHeaderConfig({
        title: title,
      });
    });
  }
}
